import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import * as Blockly from "blockly";
import { javascriptGenerator } from "blockly/javascript";

import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";

import { db } from "../firebase";
import ForexWebSocket from "../services/ForexWebSocket";

import "./AutoTrade.css";

/* =========================================================
   MARKETS
========================================================= */

const MARKETS = [
  {
    id: "volatility10",
    symbol: "R_10",
    name: "Volatility 10",
    decimals: 2,
  },
  {
    id: "volatility25",
    symbol: "R_25",
    name: "Volatility 25",
    decimals: 2,
  },
  {
    id: "volatility50",
    symbol: "R_50",
    name: "Volatility 50",
    decimals: 2,
  },
  {
    id: "volatility75",
    symbol: "R_75",
    name: "Volatility 75",
    decimals: 2,
  },
  {
    id: "volatility100",
    symbol: "R_100",
    name: "Volatility 100",
    decimals: 2,
  },
  {
    id: "volatility10-1s",
    symbol: "1HZ10V",
    name: "Volatility 10 (1s)",
    decimals: 2,
  },
  {
    id: "volatility25-1s",
    symbol: "1HZ25V",
    name: "Volatility 25 (1s)",
    decimals: 2,
  },
  {
    id: "volatility50-1s",
    symbol: "1HZ50V",
    name: "Volatility 50 (1s)",
    decimals: 2,
  },
  {
    id: "volatility75-1s",
    symbol: "1HZ75V",
    name: "Volatility 75 (1s)",
    decimals: 2,
  },
  {
    id: "volatility100-1s",
    symbol: "1HZ100V",
    name: "Volatility 100 (1s)",
    decimals: 2,
  },
];

/* =========================================================
   HELPERS
========================================================= */

const getLastDigit = (price, decimals = 2) => {
  const number = Number(price);

  if (!Number.isFinite(number)) {
    return 0;
  }

  const fixed = number.toFixed(decimals);
  const clean = fixed.replace(/\D/g, "");

  if (!clean) {
    return 0;
  }

  return Number(clean[clean.length - 1]) || 0;
};

const getUsername = () => {
  return localStorage.getItem("username")?.trim() || "";
};

const getTimeValue = (value) => {
  if (!value) return 0;

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  const time = new Date(value).getTime();

  return Number.isFinite(time) ? time : 0;
};

/* =========================================================
   BLOCK DEFINITIONS
========================================================= */

const registerBlocks = () => {
  if (Blockly.Blocks["auto_start"]) {
    return;
  }

  /* -----------------------------------------
     START
  ----------------------------------------- */

  Blockly.Blocks["auto_start"] = {
    init() {
      this.appendDummyInput()
        .appendField("START BOT");

      this.appendStatementInput("DO");

      this.setColour("#9b59b6");

      this.setTooltip(
        "Start the automated strategy"
      );
    },
  };

  /* -----------------------------------------
     BUY
  ----------------------------------------- */

  Blockly.Blocks["buy_contract"] = {
    init() {
      this.appendDummyInput()
        .appendField("buy")
        .appendField(
          new Blockly.FieldDropdown([
            ["Even", "EVEN"],
            ["Odd", "ODD"],
            ["Over", "OVER"],
            ["Under", "UNDER"],
            ["Matches", "MATCHES"],
            ["Differs", "DIFFERS"],
          ]),
          "TYPE"
        );

      this.appendValueInput("BARRIER")
        .setCheck("Number")
        .appendField("barrier");

      this.appendValueInput("DIGIT")
        .setCheck("Number")
        .appendField("digit");

      this.setPreviousStatement(true);
      this.setNextStatement(true);

      this.setColour("#50b894");
    },
  };

  /* -----------------------------------------
     START STAKE
  ----------------------------------------- */

  Blockly.Blocks["start_stake"] = {
    init() {
      this.appendValueInput("VALUE")
        .setCheck("Number")
        .appendField("start stake $");

      this.setOutput(true, "Number");
      this.setColour("#9b59b6");
    },
  };

  /* -----------------------------------------
     TAKE PROFIT
  ----------------------------------------- */

  Blockly.Blocks["take_profit"] = {
    init() {
      this.appendValueInput("VALUE")
        .setCheck("Number")
        .appendField("take profit $");

      this.setOutput(true, "Number");
      this.setColour("#9b59b6");
    },
  };

  /* -----------------------------------------
     STOP LOSS
  ----------------------------------------- */

  Blockly.Blocks["stop_loss"] = {
    init() {
      this.appendValueInput("VALUE")
        .setCheck("Number")
        .appendField("stop loss $");

      this.setOutput(true, "Number");
      this.setColour("#9b59b6");
    },
  };

  /* -----------------------------------------
     MAX TRADES
  ----------------------------------------- */

  Blockly.Blocks["max_trades"] = {
    init() {
      this.appendValueInput("VALUE")
        .setCheck("Number")
        .appendField("max trades");

      this.setOutput(true, "Number");
      this.setColour("#9b59b6");
    },
  };

  /* -----------------------------------------
     MULTIPLIER
  ----------------------------------------- */

  Blockly.Blocks["stake_multiplier"] = {
    init() {
      this.appendValueInput("VALUE")
        .setCheck("Number")
        .appendField("multiply stake by");

      this.setOutput(true, "Number");
      this.setColour("#9b59b6");
    },
  };
      /* -----------------------------------------
       LAST TRADE WON
    ----------------------------------------- */

    Blockly.Blocks["last_trade_won"] = {
      init() {
        this.appendDummyInput()
          .appendField("last trade won?");

        this.setOutput(true, "Boolean");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       LAST DIGIT
    ----------------------------------------- */

    Blockly.Blocks["last_digit"] = {
      init() {
        this.appendDummyInput()
          .appendField("last digit");

        this.setOutput(true, "Number");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       LOSS STREAK
    ----------------------------------------- */

    Blockly.Blocks["losses_row"] = {
      init() {
        this.appendDummyInput()
          .appendField("losses in a row");

        this.setOutput(true, "Number");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       WINS STREAK
    ----------------------------------------- */

    Blockly.Blocks["wins_row"] = {
      init() {
        this.appendDummyInput()
          .appendField("wins in a row");

        this.setOutput(true, "Number");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       WIN RATE
    ----------------------------------------- */

    Blockly.Blocks["win_rate"] = {
      init() {
        this.appendDummyInput()
          .appendField("win rate %");

        this.setOutput(true, "Number");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       TOTAL PROFIT
    ----------------------------------------- */

    Blockly.Blocks["total_profit"] = {
      init() {
        this.appendDummyInput()
          .appendField("total profit $");

        this.setOutput(true, "Number");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       LAST PROFIT
    ----------------------------------------- */

    Blockly.Blocks["last_profit"] = {
      init() {
        this.appendDummyInput()
          .appendField("last trade profit $");

        this.setOutput(true, "Number");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       TRADES SO FAR
    ----------------------------------------- */

    Blockly.Blocks["trades_so_far"] = {
      init() {
        this.appendDummyInput()
          .appendField("trades so far");

        this.setOutput(true, "Number");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       BALANCE
    ----------------------------------------- */

    Blockly.Blocks["balance_value"] = {
      init() {
        this.appendDummyInput()
          .appendField("balance $");

        this.setOutput(true, "Number");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       CURRENT STAKE
    ----------------------------------------- */

    Blockly.Blocks["current_stake"] = {
      init() {
        this.appendDummyInput()
          .appendField("current stake $");

        this.setOutput(true, "Number");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       IF
    ----------------------------------------- */

    Blockly.Blocks["auto_if"] = {
      init() {
        this.appendValueInput("CONDITION")
          .setCheck("Boolean")
          .appendField("if");

        this.appendStatementInput("DO")
          .appendField("do");

        this.appendStatementInput("ELSE")
          .appendField("else");

        this.setPreviousStatement(true);
        this.setNextStatement(true);
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       COMPARE
    ----------------------------------------- */

    Blockly.Blocks["compare_values"] = {
      init() {
        this.appendValueInput("A")
          .setCheck(null);

        this.appendDummyInput()
          .appendField(
            new Blockly.FieldDropdown([
              ["=", "EQ"],
              [">", "GT"],
              ["<", "LT"],
              [">=", "GTE"],
              ["<=", "LTE"],
              ["≠", "NEQ"],
            ]),
            "OP"
          );

        this.appendValueInput("B")
          .setCheck(null);

        this.setOutput(true, "Boolean");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       AND
    ----------------------------------------- */

    Blockly.Blocks["logic_and"] = {
      init() {
        this.appendValueInput("A")
          .setCheck("Boolean");

        this.appendDummyInput()
          .appendField("and");

        this.appendValueInput("B")
          .setCheck("Boolean");

        this.setOutput(true, "Boolean");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       OR
    ----------------------------------------- */

    Blockly.Blocks["logic_or"] = {
      init() {
        this.appendValueInput("A")
          .setCheck("Boolean");

        this.appendDummyInput()
          .appendField("or");

        this.appendValueInput("B")
          .setCheck("Boolean");

        this.setOutput(true, "Boolean");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       NOT
    ----------------------------------------- */

    Blockly.Blocks["logic_not"] = {
      init() {
        this.appendValueInput("VALUE")
          .setCheck("Boolean")
          .appendField("not");

        this.setOutput(true, "Boolean");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       TRUE / FALSE
    ----------------------------------------- */

    Blockly.Blocks["boolean_value"] = {
      init() {
        this.appendDummyInput()
          .appendField(
            new Blockly.FieldDropdown([
              ["true", "TRUE"],
              ["false", "FALSE"],
            ]),
            "VALUE"
          );

        this.setOutput(true, "Boolean");
        this.setColour("#5b80a5");
      },
    };

    /* -----------------------------------------
       NUMBER
    ----------------------------------------- */

    Blockly.Blocks["number_value"] = {
      init() {
        this.appendDummyInput()
          .appendField(
            new Blockly.FieldNumber(
              1,
              -999999,
              999999,
              0.01
            ),
            "VALUE"
          );

        this.setOutput(true, "Number");
        this.setColour("#8064a2");
      },
    };

    /* -----------------------------------------
       ADD
    ----------------------------------------- */

    Blockly.Blocks["math_add"] = {
      init() {
        this.appendValueInput("A")
          .setCheck("Number");

        this.appendDummyInput()
          .appendField("+");

        this.appendValueInput("B")
          .setCheck("Number");

        this.setOutput(true, "Number");
        this.setColour("#8064a2");
      },
    };

    /* -----------------------------------------
       SUBTRACT
    ----------------------------------------- */

    Blockly.Blocks["math_sub"] = {
      init() {
        this.appendValueInput("A")
          .setCheck("Number");

        this.appendDummyInput()
          .appendField("-");

        this.appendValueInput("B")
          .setCheck("Number");

        this.setOutput(true, "Number");
        this.setColour("#8064a2");
      },
    };

    /* -----------------------------------------
       MULTIPLY
    ----------------------------------------- */

    Blockly.Blocks["math_mul"] = {
      init() {
        this.appendValueInput("A")
          .setCheck("Number");

        this.appendDummyInput()
          .appendField("×");

        this.appendValueInput("B")
          .setCheck("Number");

        this.setOutput(true, "Number");
        this.setColour("#8064a2");
      },
    };

    /* -----------------------------------------
       DIVIDE
    ----------------------------------------- */

    Blockly.Blocks["math_div"] = {
      init() {
        this.appendValueInput("A")
          .setCheck("Number");

        this.appendDummyInput()
          .appendField("÷");

        this.appendValueInput("B")
          .setCheck("Number");

        this.setOutput(true, "Number");
        this.setColour("#8064a2");
      },
    };

    /* -----------------------------------------
       REPEAT
    ----------------------------------------- */

    Blockly.Blocks["repeat_times"] = {
      init() {
        this.appendValueInput("TIMES")
          .setCheck("Number")
          .appendField("repeat");

        this.appendDummyInput()
          .appendField("times");

        this.appendStatementInput("DO")
          .appendField("do");

        this.setPreviousStatement(true);
        this.setNextStatement(true);
        this.setColour("#5cb85c");
      },
    };

    /* -----------------------------------------
       WAIT TICKS
    ----------------------------------------- */

    Blockly.Blocks["wait_ticks"] = {
      init() {
        this.appendValueInput("TICKS")
          .setCheck("Number")
          .appendField("wait");

        this.appendDummyInput()
          .appendField("ticks");

        this.setPreviousStatement(true);
        this.setNextStatement(true);
        this.setColour("#8064a2");
      },
    };

    /* -----------------------------------------
       STOP
    ----------------------------------------- */

    Blockly.Blocks["stop_bot"] = {
      init() {
        this.appendDummyInput()
          .appendField("stop bot");

        this.setPreviousStatement(true);
        this.setNextStatement(true);
        this.setColour("#d66");
      },
    };
  };

  /* =========================================================
     GENERATOR
  ========================================================= */

  const registerGenerators = () => {
    javascriptGenerator.forBlock["number_value"] = (block) => {
      return [
        String(block.getFieldValue("VALUE")),
        javascriptGenerator.ORDER_ATOMIC,
      ];
    };

    javascriptGenerator.forBlock["boolean_value"] = (block) => {
      return [
        block.getFieldValue("VALUE") === "TRUE"
          ? "true"
          : "false",
        javascriptGenerator.ORDER_ATOMIC,
      ];
    };

    javascriptGenerator.forBlock["last_trade_won"] = () => [
      "ctx.lastTradeWon",
      javascriptGenerator.ORDER_ATOMIC,
    ];

    javascriptGenerator.forBlock["last_digit"] = () => [
      "ctx.lastDigit",
      javascriptGenerator.ORDER_ATOMIC,
    ];

    javascriptGenerator.forBlock["losses_row"] = () => [
      "ctx.lossesInRow",
      javascriptGenerator.ORDER_ATOMIC,
    ];

    javascriptGenerator.forBlock["wins_row"] = () => [
      "ctx.winsInRow",
      javascriptGenerator.ORDER_ATOMIC,
    ];

    javascriptGenerator.forBlock["win_rate"] = () => [
      "ctx.winRate",
      javascriptGenerator.ORDER_ATOMIC,
    ];

    javascriptGenerator.forBlock["total_profit"] = () => [
      "ctx.totalProfit",
      javascriptGenerator.ORDER_ATOMIC,
    ];

    javascriptGenerator.forBlock["last_profit"] = () => [
      "ctx.lastProfit",
      javascriptGenerator.ORDER_ATOMIC,
    ];

    javascriptGenerator.forBlock["trades_so_far"] = () => [
      "ctx.trades",
      javascriptGenerator.ORDER_ATOMIC,
    ];

    javascriptGenerator.forBlock["balance_value"] = () => [
      "ctx.balance",
      javascriptGenerator.ORDER_ATOMIC,
    ];

    javascriptGenerator.forBlock["current_stake"] = () => [
      "ctx.currentStake",
      javascriptGenerator.ORDER_ATOMIC,
    ];

    javascriptGenerator.forBlock["start_stake"] = (block) => {
      const value =
        javascriptGenerator.valueToCode(
          block,
          "VALUE",
          javascriptGenerator.ORDER_ATOMIC
        ) || "1";

      return [value, javascriptGenerator.ORDER_ATOMIC];
    };

    javascriptGenerator.forBlock["take_profit"] = (block) => {
      const value =
        javascriptGenerator.valueToCode(
          block,
          "VALUE",
          javascriptGenerator.ORDER_ATOMIC
        ) || "20";

      return [value, javascriptGenerator.ORDER_ATOMIC];
    };

    javascriptGenerator.forBlock["stop_loss"] = (block) => {
      const value =
        javascriptGenerator.valueToCode(
          block,
          "VALUE",
          javascriptGenerator.ORDER_ATOMIC
        ) || "50";

      return [value, javascriptGenerator.ORDER_ATOMIC];
    };

    javascriptGenerator.forBlock["max_trades"] = (block) => {
      const value =
        javascriptGenerator.valueToCode(
          block,
          "VALUE",
          javascriptGenerator.ORDER_ATOMIC
        ) || "100";

      return [value, javascriptGenerator.ORDER_ATOMIC];
    };

    javascriptGenerator.forBlock["stake_multiplier"] = (block) => {
      const value =
        javascriptGenerator.valueToCode(
          block,
          "VALUE",
          javascriptGenerator.ORDER_ATOMIC
        ) || "2";

      return [value, javascriptGenerator.ORDER_ATOMIC];
    };
    javascriptGenerator.forBlock["compare_values"] = (block) => {
      const a =
        javascriptGenerator.valueToCode(
          block,
          "A",
          javascriptGenerator.ORDER_RELATIONAL
        ) || "0";

      const b =
        javascriptGenerator.valueToCode(
          block,
          "B",
          javascriptGenerator.ORDER_RELATIONAL
        ) || "0";

      const op = block.getFieldValue("OP");

      const operators = {
        EQ: "===",
        GT: ">",
        LT: "<",
        GTE: ">=",
        LTE: "<=",
        NEQ: "!==",
      };

      return [
        `(${a} ${operators[op] || "==="} ${b})`,
        javascriptGenerator.ORDER_RELATIONAL,
      ];
    };

    javascriptGenerator.forBlock["logic_and"] = (block) => {
      const a =
        javascriptGenerator.valueToCode(
          block,
          "A",
          javascriptGenerator.ORDER_LOGICAL_AND
        ) || "false";

      const b =
        javascriptGenerator.valueToCode(
          block,
          "B",
          javascriptGenerator.ORDER_LOGICAL_AND
        ) || "false";

      return [
        `(${a} && ${b})`,
        javascriptGenerator.ORDER_LOGICAL_AND,
      ];
    };

    javascriptGenerator.forBlock["logic_or"] = (block) => {
      const a =
        javascriptGenerator.valueToCode(
          block,
          "A",
          javascriptGenerator.ORDER_LOGICAL_OR
        ) || "false";

      const b =
        javascriptGenerator.valueToCode(
          block,
          "B",
          javascriptGenerator.ORDER_LOGICAL_OR
        ) || "false";

      return [
        `(${a} || ${b})`,
        javascriptGenerator.ORDER_LOGICAL_OR,
      ];
    };

    javascriptGenerator.forBlock["logic_not"] = (block) => {
      const value =
        javascriptGenerator.valueToCode(
          block,
          "VALUE",
          javascriptGenerator.ORDER_LOGICAL_NOT
        ) || "false";

      return [
        `(!${value})`,
        javascriptGenerator.ORDER_LOGICAL_NOT,
      ];
    };

    javascriptGenerator.forBlock["math_add"] = (block) => {
      const a =
        javascriptGenerator.valueToCode(
          block,
          "A",
          javascriptGenerator.ORDER_ADDITION
        ) || "0";

      const b =
        javascriptGenerator.valueToCode(
          block,
          "B",
          javascriptGenerator.ORDER_ADDITION
        ) || "0";

      return [
        `(${a}+${b})`,
        javascriptGenerator.ORDER_ADDITION,
      ];
    };

    javascriptGenerator.forBlock["math_sub"] = (block) => {
      const a =
        javascriptGenerator.valueToCode(
          block,
          "A",
          javascriptGenerator.ORDER_SUBTRACTION
        ) || "0";

      const b =
        javascriptGenerator.valueToCode(
          block,
          "B",
          javascriptGenerator.ORDER_SUBTRACTION
        ) || "0";

      return [
        `(${a}-${b})`,
        javascriptGenerator.ORDER_SUBTRACTION,
      ];
    };

    javascriptGenerator.forBlock["math_mul"] = (block) => {
      const a =
        javascriptGenerator.valueToCode(
          block,
          "A",
          javascriptGenerator.ORDER_MULTIPLICATION
        ) || "0";

      const b =
        javascriptGenerator.valueToCode(
          block,
          "B",
          javascriptGenerator.ORDER_MULTIPLICATION
        ) || "0";

      return [
        `(${a}*${b})`,
        javascriptGenerator.ORDER_MULTIPLICATION,
      ];
    };

    javascriptGenerator.forBlock["math_div"] = (block) => {
      const a =
        javascriptGenerator.valueToCode(
          block,
          "A",
          javascriptGenerator.ORDER_DIVISION
        ) || "0";

      const b =
        javascriptGenerator.valueToCode(
          block,
          "B",
          javascriptGenerator.ORDER_DIVISION
        ) || "1";

      return [
        `(${a}/${b})`,
        javascriptGenerator.ORDER_DIVISION,
      ];
    };

    javascriptGenerator.forBlock["buy_contract"] = (block) => {
      const type = block.getFieldValue("TYPE");

      const barrier =
        javascriptGenerator.valueToCode(
          block,
          "BARRIER",
          javascriptGenerator.ORDER_ATOMIC
        ) || "5";

      const digit =
        javascriptGenerator.valueToCode(
          block,
          "DIGIT",
          javascriptGenerator.ORDER_ATOMIC
        ) || "0";

      return `await ctx.buy("${type}", ${barrier}, ${digit});\n`;
    };

    javascriptGenerator.forBlock["auto_if"] = (block) => {
      const condition =
        javascriptGenerator.valueToCode(
          block,
          "CONDITION",
          javascriptGenerator.ORDER_NONE
        ) || "false";

      const statements =
        javascriptGenerator.statementToCode(block, "DO");

      const elseStatements =
        javascriptGenerator.statementToCode(block, "ELSE");

      return (
        `if (${condition}) {\n` +
        statements +
        `} else {\n` +
        elseStatements +
        `}\n`
      );
    };

    javascriptGenerator.forBlock["repeat_times"] = (block) => {
      const times =
        javascriptGenerator.valueToCode(
          block,
          "TIMES",
          javascriptGenerator.ORDER_ATOMIC
        ) || "1";

      const statements =
        javascriptGenerator.statementToCode(block, "DO");

      return (
        `for (let i = 0; i < ${times}; i++) {\n` +
        statements +
        `}\n`
      );
    };

    javascriptGenerator.forBlock["wait_ticks"] = (block) => {
      const ticks =
        javascriptGenerator.valueToCode(
          block,
          "TICKS",
          javascriptGenerator.ORDER_ATOMIC
        ) || "1";

      return `await ctx.waitTicks(${ticks});\n`;
    };

    javascriptGenerator.forBlock["stop_bot"] = () => {
      return "ctx.stop();\n";
    };

    javascriptGenerator.forBlock["auto_start"] = (block) => {
      const statements =
        javascriptGenerator.statementToCode(block, "DO");

      return (
        `while (!ctx.stopped) {\n` +
        statements +
        `\nif (ctx.stopped) break;\n` +
        `await ctx.waitTicks(1);\n` +
        `}\n`
      );
    };
  };

  /* =========================================================
     TOOLBOX
  ========================================================= */

  const TOOLBOX = {
    kind: "categoryToolbox",
    contents: [
      {
        kind: "category",
        name: "Purchase",
        colour: "#50b894",
        contents: [
          {
            kind: "block",
            type: "buy_contract",
          },
        ],
      },
      {
        kind: "category",
        name: "Stake",
        colour: "#9b59b6",
        contents: [
          {
            kind: "block",
            type: "start_stake",
            inputs: {
              VALUE: {
                shadow: {
                  type: "number_value",
                  fields: { VALUE: 1 },
                },
              },
            },
          },
          {
            kind: "block",
            type: "take_profit",
            inputs: {
              VALUE: {
                shadow: {
                  type: "number_value",
                  fields: { VALUE: 20 },
                },
              },
            },
          },
          {
            kind: "block",
            type: "stop_loss",
            inputs: {
              VALUE: {
                shadow: {
                  type: "number_value",
                  fields: { VALUE: 50 },
                },
              },
            },
          },
          {
            kind: "block",
            type: "max_trades",
            inputs: {
              VALUE: {
                shadow: {
                  type: "number_value",
                  fields: { VALUE: 100 },
                },
              },
            },
          },
          {
            kind: "block",
            type: "stake_multiplier",
            inputs: {
              VALUE: {
                shadow: {
                  type: "number_value",
                  fields: { VALUE: 2 },
                },
              },
            },
          },
        ],
      },
      {
        kind: "category",
        name: "Values",
        colour: "#5b80a5",
        contents: [
          { kind: "block", type: "last_trade_won" },
          { kind: "block", type: "last_digit" },
          { kind: "block", type: "losses_row" },
          { kind: "block", type: "wins_row" },
          { kind: "block", type: "win_rate" },
          { kind: "block", type: "total_profit" },
          { kind: "block", type: "last_profit" },
          { kind: "block", type: "trades_so_far" },
          { kind: "block", type: "balance_value" },
          { kind: "block", type: "current_stake" },
        ],
      },
      {
        kind: "category",
        name: "Analysis",
        colour: "#5b80a5",
        contents: [
          { kind: "block", type: "last_digit" },
          { kind: "block", type: "win_rate" },
          { kind: "block", type: "losses_row" },
          { kind: "block", type: "wins_row" },
        ],
      },
      {
        kind: "category",
        name: "Logic",
        colour: "#5b80a5",
        contents: [
          { kind: "block", type: "auto_if" },
          { kind: "block", type: "compare_values" },
          { kind: "block", type: "logic_and" },
          { kind: "block", type: "logic_or" },
          { kind: "block", type: "logic_not" },
          { kind: "block", type: "boolean_value" },
        ],
      },
      {
        kind: "category",
        name: "Math",
        colour: "#8064a2",
        contents: [
          { kind: "block", type: "number_value" },
          { kind: "block", type: "math_add" },
          { kind: "block", type: "math_sub" },
          { kind: "block", type: "math_mul" },
          { kind: "block", type: "math_div" },
        ],
      },
      {
        kind: "category",
        name: "Flow",
        colour: "#5cb85c",
        contents: [
          { kind: "block", type: "repeat_times" },
          { kind: "block", type: "wait_ticks" },
          { kind: "block", type: "stop_bot" },
        ],
      },
      {
        kind: "category",
        name: "Variables",
        colour: "#b56576",
        custom: "VARIABLE",
      },
    ],
  };
    /* =========================================================
     DEFAULT BOT
  ========================================================= */

  const DEFAULT_BOT_XML = `
  <xml xmlns="https://developers.google.com/blockly/xml">
    <block type="auto_start" x="40" y="40">
      <statement name="DO">
        <block type="buy_contract">
          <field name="TYPE">UNDER</field>
          <value name="BARRIER">
            <shadow type="number_value">
              <field name="VALUE">8</field>
            </shadow>
          </value>
          <next>
            <block type="auto_if">
              <value name="CONDITION">
                <block type="last_trade_won"/>
              </value>
              <statement name="DO">
                <block type="start_stake">
                  <value name="VALUE">
                    <shadow type="number_value">
                      <field name="VALUE">1</field>
                    </shadow>
                  </value>
                </block>
              </statement>
              <statement name="ELSE">
                <block type="stake_multiplier">
                  <value name="VALUE">
                    <shadow type="number_value">
                      <field name="VALUE">2</field>
                    </shadow>
                  </value>
                </block>
              </statement>
            </block>
          </next>
        </block>
      </statement>
    </block>
  </xml>
  `;

  /* =========================================================
     COMPONENT
  ========================================================= */

  export default function AutoTrade() {
    const navigate = useNavigate();

    const workspaceRef = useRef(null);
    const workspaceInstance = useRef(null);

    const botRunningRef = useRef(false);
    const currentPriceRef = useRef(null);
    const currentEpochRef = useRef(null);
    const currentDigitRef = useRef(0);
    const tickWaitersRef = useRef([]);
    const lastTradeWonRef = useRef(false);

    const selectedMarketRef = useRef(
      "volatility10-1s"
    );

    /* =======================================================
       STATE
    ======================================================= */

    const [activeCategory, setActiveCategory] =
      useState("Purchase");

    const [builderMode, setBuilderMode] =
      useState(true);

    const [running, setRunning] =
      useState(false);

    const [selectedMarketId, setSelectedMarketId] =
      useState("volatility10-1s");

    const [liveMarkets, setLiveMarkets] =
      useState({});

    const [balance, setBalance] = useState(0);

    const [username, setUsername] =
      useState(getUsername());

    const [logs, setLogs] = useState([]);

    const [savedBots, setSavedBots] = useState([]);

    const [showTemplates, setShowTemplates] =
      useState(false);

    const [showLoad, setShowLoad] = useState(false);

    const [stats, setStats] = useState({
      trades: 0,
      wins: 0,
      losses: 0,
      totalProfit: 0,
      lastProfit: 0,
      lossesInRow: 0,
      winsInRow: 0,
      currentStake: 1,
      lastTradeWon: false,
    });

    /* =======================================================
       SELECTED MARKET
    ======================================================= */

    const selectedMarket = useMemo(() => {
      return (
        MARKETS.find(
          (market) =>
            market.id === selectedMarketId
        ) || MARKETS[0]
      );
    }, [selectedMarketId]);

    /* =======================================================
       LIVE PRICE
    ======================================================= */

    const selectedLiveMarket =
      liveMarkets[selectedMarket.symbol];

    const currentPrice =
      selectedLiveMarket?.price ?? null;

    const currentDigit =
      currentPrice !== null
        ? getLastDigit(
            currentPrice,
            selectedMarket.decimals
          )
        : 0;

    /* =======================================================
       LOG
    ======================================================= */

    const addLog = useCallback(
      (message, type = "info") => {
        setLogs((previous) => [
          {
            id: Date.now() + Math.random(),
            time: new Date().toLocaleTimeString(),
            message,
            type,
          },
          ...previous,
        ].slice(0, 100));
      },
      []
    );

    /* =======================================================
       USER
    ======================================================= */

    const loadUser = useCallback(async () => {
      const currentUsername = getUsername();

      if (!currentUsername) {
        setBalance(0);
        return null;
      }

      try {
        const usersRef = collection(db, "users");

        const q = query(
          usersRef,
          where("username", "==", currentUsername)
        );

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setBalance(0);
          return null;
        }

        const userDoc = snapshot.docs[0];
        const data = userDoc.data();

        const user = {
          id: userDoc.id,
          username: currentUsername,
          balance:
            Number(data.usdtBalanceOther) || 0,
        };

        setUsername(currentUsername);
        setBalance(user.balance);

        return user;
      } catch (error) {
        console.error(
          "AutoTrade user error:",
          error
        );

        return null;
      }
    }, []);

    useEffect(() => {
      loadUser();
    }, [loadUser]);

    /* =======================================================
       FIREBASE ORDERS LISTENER
    ======================================================= */

    useEffect(() => {
      const currentUsername = getUsername();

      if (!currentUsername) {
        return undefined;
      }

      const ordersRef = collection(
        db,
        "volatilityorder"
      );

      const q = query(
        ordersRef,
        where("username", "==", currentUsername)
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const orders = snapshot.docs.map(
            (item) => ({
              id: item.id,
              ...item.data(),
            })
          );

          const wins = orders.filter(
            (order) =>
              order.status === "won" ||
              order.result === "won"
          );

          const losses = orders.filter(
            (order) =>
              order.status === "lost" ||
              order.result === "lost"
          );

          const totalProfit = orders.reduce(
            (total, order) =>
              total +
              (Number(order.profit) || 0),
            0
          );

          setStats((previous) => ({
            ...previous,
            trades: orders.length,
            wins: wins.length,
            losses: losses.length,
            totalProfit,
          }));
        },
        (error) => {
          console.error(
            "AutoTrade orders listener error:",
            error
          );
        }
      );

      return unsubscribe;
    }, []);
        /* =======================================================
       FOREX WEBSOCKET
    ======================================================= */

   
useEffect(() => {
  let mounted = true;
  let subscriptionsRequested = false;

  const requestMarketTicks = () => {
    if (subscriptionsRequested || !mounted) return;

    subscriptionsRequested = true;

    MARKETS.forEach((market) => {
      ForexWebSocket.ticks(market.symbol);
    });

    console.log(
      "Requested live ticks for:",
      MARKETS.map((market) => market.symbol)
    );
  };

  const unsubscribe = ForexWebSocket.onTick((data) => {
    if (!mounted || !data) return;

    // Subscribe once the WebSocket is connected.
    if (
      data.type === "connection" &&
      data.connected === true
    ) {
      requestMarketTicks();
      return;
    }

    // Ignore non-tick messages.
    if (data.msg_type !== "tick" || !data.tick) {
      return;
    }

    const tick = data.tick;
    const symbol = tick.symbol;
    const quote = Number(tick.quote);
    const epoch = tick.epoch;

    if (
      !symbol ||
      !Number.isFinite(quote)
    ) {
      return;
    }

    const market = MARKETS.find(
      (item) => item.symbol === symbol
    );

    if (!market) return;

    const digit = getLastDigit(
      quote,
      market.decimals
    );

    const tickEpoch =
      epoch || Math.floor(Date.now() / 1000);

    setLiveMarkets((previous) => ({
      ...previous,
      [symbol]: {
        price: quote,
        epoch: tickEpoch,
        digit,
      },
    }));

    // selectedMarketRef stores the market ID,
    // not the Deriv symbol.
    if (market.id === selectedMarketRef.current) {
      currentPriceRef.current = quote;
      currentEpochRef.current = tickEpoch;
      currentDigitRef.current = digit;

      const waiters = tickWaitersRef.current;
      tickWaitersRef.current = [];

      waiters.forEach((resolve) => {
        resolve({
          price: quote,
          digit,
          epoch: tickEpoch,
        });
      });
    }
  });

  ForexWebSocket.connect();

  // Handles a socket that is already connected.
  if (ForexWebSocket.isConnected?.()) {
    requestMarketTicks();
  }

  return () => {
    mounted = false;

    if (typeof unsubscribe === "function") {
      unsubscribe();
    }

    // Do not close the shared WebSocket here.
  };
}, []);


    /* =======================================================
       MARKET CHANGE
    ======================================================= */

    const handleMarketChange = (event) => {
      const nextId = event.target.value;

      selectedMarketRef.current = nextId;

      setSelectedMarketId(nextId);

      addLog(
        `Market changed to ${
          MARKETS.find(
            (item) => item.id === nextId
          )?.name
        }`
      );
    };

    /* =======================================================
       WAIT TICKS
    ======================================================= */

    const waitTicks = useCallback(
      async (ticks = 1) => {
        const count = Math.max(
          1,
          Number(ticks) || 1
        );

        for (let i = 0; i < count; i++) {
          if (!botRunningRef.current) {
            return;
          }

          await new Promise((resolve) => {
            tickWaitersRef.current.push(resolve);

            setTimeout(() => {
              const index =
                tickWaitersRef.current.indexOf(
                  resolve
                );

              if (index !== -1) {
                tickWaitersRef.current.splice(
                  index,
                  1
                );

                resolve({
                  price: currentPriceRef.current,
                  digit: currentDigitRef.current,
                  epoch: currentEpochRef.current,
                });
              }
            }, 5000);
          });
        }
      },
      []
    );

    /* =======================================================
       CLOSE TRADE
    ======================================================= */

    
    const settleTrade =
      useCallback(
        async ({
          orderId,
          stake,
          won,
          entryPrice,
          entryDigit,
          exitPrice,
          exitDigit,
          contractType,
          market,
        }) => {
          const currentUsername =
            getUsername();

          if (
            !currentUsername
          ) {
            return;
          }

          const user =
            await loadUser();

          if (!user) {
            return;
          }

          const userRef =
            doc(
              db,
              "users",
              user.id
            );

          const orderRef =
            doc(
              db,
              "volatilityorder",
              orderId
            );

          let finalBalance =
            0;

          let profit = 0;

          let payout = 0;

          await runTransaction(
            db,
            async (
              transaction
            ) => {
              const userSnapshot =
                await transaction.get(
                  userRef
                );

              if (
                !userSnapshot.exists()
              ) {
                throw new Error(
                  "USER_NOT_FOUND"
                );
              }

              const userData =
                userSnapshot.data();

              const currentBalance =
                Number(
                  userData.usdtBalanceOther
                ) || 0;

              if (won) {
                payout =
                  Number(
                    (
                      stake *
                      1.98
                    ).toFixed(2)
                  );

                profit =
                  Number(
                    (
                      stake *
                      0.98
                    ).toFixed(2)
                  );

                finalBalance =
                  Number(
                    (
                      currentBalance +
                      payout
                    ).toFixed(2)
                  );
              } else {
                payout = 0;

                profit =
                  Number(
                    (-stake).toFixed(
                      2
                    )
                  );

                finalBalance =
                  Number(
                    currentBalance.toFixed(
                      2
                    )
                  );
              }

              transaction.update(
                userRef,
                {
                  usdtBalanceOther:
                    finalBalance,
                }
              );

              transaction.update(
                orderRef,
                {
                  status:
                    won
                      ? "WON"
                      : "LOST",

                  payout,

                  profit,

                  result:
                    profit,

                  exitPrice,

                  exitDigit,

                  exitTick:
                    currentEpochRef.current,

                  exitTickId:
                    currentEpochRef.current,

                  balanceAfter:
                    finalBalance,

                  updatedAt:
                    serverTimestamp(),

                  closedAt:
                    serverTimestamp(),

                  entryPrice,
                  entryDigit,

                  contractType,

                  marketSymbol:
                    market.symbol,

                  marketName:
                    market.name,
                }
              );
            }
          );

          setBalance(
            finalBalance
          );

          setStats(
            (previous) => {
              const nextWins =
                won
                  ? previous.wins +
                    1
                  : previous.wins;

              const nextLosses =
                won
                  ? previous.losses
                  : previous.losses +
                    1;

              return {
                ...previous,

                trades:
                  previous.trades +
                  1,

                wins: nextWins,

                losses:
                  nextLosses,

                totalProfit:
                  Number(
                    (
                      previous.totalProfit +
                      profit
                    ).toFixed(2)
                  ),

                lastProfit:
                  profit,

                lastTradeWon:
                  won,

                winsInRow:
                  won
                    ? previous.winsInRow +
                      1
                    : 0,

                lossesInRow:
                  won
                    ? 0
                    : previous.lossesInRow +
                      1,
              };
            }
          );

          lastTradeWonRef.current =
            won;

          addLog(
            `${won ? "WIN" : "LOSS"} • ${
              market.name
            } • ${
              contractType
            } • ${
              exitDigit
            } • ${
              profit >= 0
                ? "+"
                : ""
            }$${profit.toFixed(
              2
            )}`,
            won
              ? "win"
              : "loss"
          );

          return {
            won,
            profit,
            payout,
            balance:
              finalBalance,
          };
        },
        [addLog, loadUser]
      );

    /* =======================================================
       BUY CONTRACT
    ======================================================= */

    const buyContract =
      useCallback(
        async (
          type,
          barrier = 5,
          digit = 0
        ) => {
          if (
            !botRunningRef.current
          ) {
            return null;
          }

          const user =
            await loadUser();

          if (!user) {
            addLog(
              "User account haijapatikana.",
              "error"
            );

            throw new Error(
              "USER_NOT_FOUND"
            );
          }

          const market =
            MARKETS.find(
              (item) =>
                item.id ===
                selectedMarketRef.current
            ) ||
            MARKETS[0];

          const stake =
            Math.max(
              0.01,
              Number(
                stats.currentStake
              ) || 1
            );

          if (
            user.balance <
            stake
          ) {
            addLog(
              "USDT balance haitoshi.",
              "error"
            );

            botRunningRef.current =
              false;

            setRunning(false);

            throw new Error(
              "INSUFFICIENT_BALANCE"
            );
          }

          const entryPrice =
            Number(
              currentPriceRef.current
            );

          if (
            !Number.isFinite(
              entryPrice
            )
          ) {
            addLog(
              "Subiri live tick ipatikane.",
              "error"
            );

            throw new Error(
              "NO_LIVE_PRICE"
            );
          }

          const entryDigit =
            Number(
              currentDigitRef.current
            );

          const orderRef =
            doc(
              collection(
                db,
                "volatilityorder"
              )
            );

          const tradeId =
            `${Date.now()}-${Math.random()
              .toString(36)
              .slice(2)}`;

          let finalBalance =
            0;

          await runTransaction(
            db,
            async (
              transaction
            ) => {
              const userSnapshot =
                await transaction.get(
                  user.id
                    ? doc(
                        db,
                        "users",
                        user.id
                      )
                    : doc(
                        db,
                        "users",
                        user.id
                      )
                );

              if (
                !userSnapshot.exists()
              ) {
                throw new Error(
                  "USER_NOT_FOUND"
                );
              }

              const data =
                userSnapshot.data();

              const firestoreBalance =
                Number(
                  data.usdtBalanceOther
                ) || 0;

              if (
                firestoreBalance <
                stake
              ) {
                throw new Error(
                  "INSUFFICIENT_BALANCE"
                );
              }

              finalBalance =
                Number(
                  (
                    firestoreBalance -
                    stake
                  ).toFixed(2)
                );

              transaction.update(
                doc(
                  db,
                  "users",
                  user.id
                ),
                {
                  usdtBalanceOther:
                    finalBalance,
                }
              );

              transaction.set(
                orderRef,
                {
                  username:
                    user.username,

                  userId:
                    user.id,

                  localTradeId:
                    tradeId,

                  orderType:
                    "VOLATILITY",

                  status:
                    "OPEN",

                  tradeType:
                    type,

                  contractType:
                    type,

                  contractLabel:
                    type,

                  marketId:
                    market.id,

                  marketSymbol:
                    market.symbol,

                  marketName:
                    market.name,

                  stake:
                    Number(
                      stake.toFixed(2)
                    ),

                  payout: 0,

                  profit: 0,

                  result: 0,

                  entryPrice,

                  entryDigit,

                  entryTick:
                    currentEpochRef.current,

                  entryTickId:
                    currentEpochRef.current,

                  exitPrice:
                    null,

                  exitDigit:
                    null,

                  exitTick:
                    null,

                  exitTickId:
                    null,

                  barrier:
                    Number(
                      barrier
                    ),

                  prediction:
                    Number(
                      digit
                    ),

                  balanceBefore:
                    Number(
                      (
                        firestoreBalance
                      ).toFixed(2)
                    ),

                  balanceAfter:
                    finalBalance,

                  currency:
                    "USDT",

                  tradingMode:
                    "auto",

                  createdAt:
                    serverTimestamp(),

                  updatedAt:
                    serverTimestamp(),

                  closedAt:
                    null,
                }
              );
            }
          );

          setBalance(
            finalBalance
          );

          addLog(
            `BUY ${type} • ${
              market.name
            } • Digit ${entryDigit} • Stake $${stake.toFixed(
              2
            )}`,
            "trade"
          );

          /*
           * Kwa mfumo wa Volatility wa
           * screenshots, tunasubiri tick
           * inayofuata ndipo contract
           * ifungwe.
           */

          await waitTicks(1);

          if (
            !botRunningRef.current
          ) {
            return null;
          }

          const exitPrice =
            Number(
              currentPriceRef.current
            );

          const exitDigit =
            Number(
              currentDigitRef.current
            );

          let won = false;

          switch (type) {
            case "EVEN":
              won =
                exitDigit % 2 ===
                0;
              break;

            case "ODD":
              won =
                exitDigit % 2 !==
                0;
              break;

            case "OVER":
              won =
                exitDigit >
                Number(barrier);
              break;

            case "UNDER":
              won =
                exitDigit <
                Number(barrier);
              break;

            case "MATCHES":
              won =
                exitDigit ===
                Number(digit);
              break;

            case "DIFFERS":
              won =
                exitDigit !==
                Number(digit);
              break;

            default:
              won = false;
          }

          const result =
            await settleTrade({
              orderId:
                orderRef.id,

              stake,

              won,

              entryPrice,

              entryDigit,

              exitPrice,

              exitDigit,

              contractType:
                type,

              market,
            });

          return result;
        },
        [
          addLog,
          loadUser,
          settleTrade,
          stats.currentStake,
          waitTicks,
        ]
      );
          /* =======================================================
       BLOCKLY CONTEXT
    ======================================================= */

    const createContext =
    useCallback(() => {
      const context = {
        stopped: false,

        lastDigit:
          currentDigitRef.current,

        lastTradeWon:
          lastTradeWonRef.current,

        lossesInRow:
          stats.lossesInRow,

        winsInRow:
          stats.winsInRow,

        winRate:
          stats.wins +
          stats.losses >
          0
            ? (
                (stats.wins /
                  (stats.wins +
                    stats.losses)) *
                100
              )
            : 0,

        totalProfit:
          stats.totalProfit,

        lastProfit:
          stats.lastProfit,

        trades:
          stats.trades,

        balance,

        currentStake:
          stats.currentStake,

        waitTicks,

        stop() {
          context.stopped =
            true;

          botRunningRef.current =
            false;

          setRunning(false);

          addLog(
            "Bot stopped.",
            "info"
          );
        },

        async buy(
          type,
          barrier,
          digit
        ) {
          context.lastDigit =
            currentDigitRef.current;

          context.lastTradeWon =
            lastTradeWonRef.current;

          context.lossesInRow =
            stats.lossesInRow;

          context.winsInRow =
            stats.winsInRow;

          context.winRate =
            stats.wins +
            stats.losses >
            0
              ? (
                  (stats.wins /
                    (stats.wins +
                      stats.losses)) *
                  100
                )
              : 0;

          context.totalProfit =
            stats.totalProfit;

          context.lastProfit =
            stats.lastProfit;

          context.trades =
            stats.trades;

          context.balance =
            balance;

          context.currentStake =
            stats.currentStake;

          const result =
            await buyContract(
              type,
              barrier,
              digit
            );

          if (!result) {
            return null;
          }

          context.lastTradeWon =
            result.won;

          context.lastProfit =
            result.profit;

          context.balance =
            result.balance;

          return result;
        },
      };

      return context;
    }, [
      addLog,
      balance,
      buyContract,
      stats,
      waitTicks,
    ]);

  /* =======================================================
     RUN BOT
  ======================================================= */

  const runBot =
    useCallback(async () => {
      if (
        botRunningRef.current
      ) {
        return;
      }

      if (
        !workspaceInstance.current
      ) {
        addLog(
          "Blockly workspace haijawa tayari.",
          "error"
        );

        return;
      }

      const code =
        javascriptGenerator.workspaceToCode(
          workspaceInstance.current
        );

      if (!code.trim()) {
        addLog(
          "Weka blocks kwanza.",
          "error"
        );

        return;
      }

      const user =
        await loadUser();

      if (!user) {
        addLog(
          "Login/user account haijapatikana.",
          "error"
        );

        return;
      }

      if (
        !Number.isFinite(
          currentPriceRef.current
        )
      ) {
        addLog(
          "Subiri Volatility live tick ipatikane.",
          "error"
        );

        return;
      }

      botRunningRef.current =
        true;

      setRunning(true);

      addLog(
        `Bot started • ${selectedMarket.name}`,
        "info"
      );

      try {
        const ctx =
          createContext();

        const runner =
          new Function(
            "ctx",
            `"use strict"; return (async () => { ${code} })();`
          );

        await runner(ctx);

        if (
          botRunningRef.current
        ) {
          botRunningRef.current =
            false;

          setRunning(false);

          addLog(
            "Bot finished.",
            "info"
          );
        }
      } catch (error) {
        console.error(
          "AutoTrade bot error:",
          error
        );

        botRunningRef.current =
          false;

        setRunning(false);

        addLog(
          error?.message ||
            "Bot execution error.",
          "error"
        );
      }
    }, [
      addLog,
      createContext,
      loadUser,
      selectedMarket.name,
    ]);

  /* =======================================================
     STOP
  ======================================================= */

  const stopBot =
    useCallback(() => {
      botRunningRef.current =
        false;

      setRunning(false);

      tickWaitersRef.current.forEach(
        (resolve) =>
          resolve({
            stopped: true,
          })
      );

      tickWaitersRef.current =
        [];

      addLog(
        "Bot stopped manually.",
        "info"
      );
    }, [addLog]);

  /* =======================================================
     SAVE BOT
  ======================================================= */

  const saveBot =
    useCallback(() => {
      if (
        !workspaceInstance.current
      ) {
        return;
      }

      const xml =
        Blockly.utils.xml.domToText(
          Blockly.Xml.workspaceToDom(
            workspaceInstance.current
          )
        );

      const name =
        window.prompt(
          "Bot name:",
          `Volatility Bot ${
            savedBots.length + 1
          }`
        );

      if (!name) {
        return;
      }

      const bot = {
        id:
          Date.now().toString(),
        name,
        marketId:
          selectedMarketId,
        xml,
        createdAt:
          new Date().toISOString(),
      };

      const nextBots = [
        ...savedBots,
        bot,
      ];

      setSavedBots(
        nextBots
      );

      localStorage.setItem(
        "volatilityAutoBots",
        JSON.stringify(
          nextBots
        )
      );

      addLog(
        `Bot "${name}" saved.`,
        "info"
      );
    }, [
      addLog,
      savedBots,
      selectedMarketId,
    ]);

  /* =======================================================
     LOAD SAVED BOTS
  ======================================================= */

  useEffect(() => {
    try {
      const stored =
        localStorage.getItem(
          "volatilityAutoBots"
        );

      if (stored) {
        setSavedBots(
          JSON.parse(stored)
        );
      }
    } catch (error) {
      console.error(
        "Load saved bots error:",
        error
      );
    }
  }, []);

  /* =======================================================
     LOAD XML
  ======================================================= */

  const loadXml =
    useCallback(
      (xmlText) => {
        if (
          !workspaceInstance.current
        ) {
          return;
        }

        try {
          workspaceInstance.current.clear();

          const dom =
            Blockly.utils.xml.textToDom(
              xmlText
            );

          Blockly.Xml.domToWorkspace(
            dom,
            workspaceInstance.current
          );

          addLog(
            "Bot loaded.",
            "info"
          );
        } catch (error) {
          console.error(
            "Load XML error:",
            error
          );

          addLog(
            "Bot XML haijasomeka.",
            "error"
          );
        }
      },
      [addLog]
    );
        /* =======================================================
       TEMPLATES
    ======================================================= */

    const templates = [
      {
        id: "under8",
        name: "UNDER 8",
        description:
          "Under 8 strategy",
        xml: DEFAULT_BOT_XML,
      },

      {
        id: "over5",
        name: "OVER 5",
        description:
          "Over 5 strategy",
        xml: `
          <xml xmlns="https://developers.google.com/blockly/xml">
            <block type="auto_start" x="40" y="40">
              <statement name="DO">
                <block type="buy_contract">
                  <field name="TYPE">OVER</field>
                  <value name="BARRIER">
                    <shadow type="number_value">
                      <field name="VALUE">5</field>
                    </shadow>
                  </value>
                </block>
              </statement>
            </block>
          </xml>
        `,
      },

      {
        id: "even",
        name: "EVEN",
        description:
          "Even digit strategy",
        xml: `
          <xml xmlns="https://developers.google.com/blockly/xml">
            <block type="auto_start" x="40" y="40">
              <statement name="DO">
                <block type="buy_contract">
                  <field name="TYPE">EVEN</field>
                </block>
              </statement>
            </block>
          </xml>
        `,
      },

      {
        id: "odd",
        name: "ODD",
        description:
          "Odd digit strategy",
        xml: `
          <xml xmlns="https://developers.google.com/blockly/xml">
            <block type="auto_start" x="40" y="40">
              <statement name="DO">
                <block type="buy_contract">
                  <field name="TYPE">ODD</field>
                </block>
              </statement>
            </block>
          </xml>
        `,
      },
    ];

    /* =======================================================
       BLOCKLY INITIALIZE
    ======================================================= */

    useEffect(() => {
      registerBlocks();
      registerGenerators();

      if (
        !workspaceRef.current
      ) {
        return undefined;
      }

      const workspace =
        Blockly.inject(
          workspaceRef.current,
          {
            toolbox: TOOLBOX,

            trashcan: true,

            scrollbars: true,

            sounds: false,

            grid: {
              spacing: 20,
              length: 3,
              colour:
                "#43202f",
              snap: true,
            },

            zoom: {
              controls: true,
              wheel: true,
              startScale: 0.85,
              maxScale: 1.5,
              minScale: 0.45,
              scaleSpeed: 1.1,
            },
          }
        );

      workspaceInstance.current =
        workspace;

      try {
        const dom =
          Blockly.utils.xml.textToDom(
            DEFAULT_BOT_XML
          );

        Blockly.Xml.domToWorkspace(
          dom,
          workspace
        );
      } catch (error) {
        console.error(
          "Default Blockly XML error:",
          error
        );
      }

      return () => {
        if (
          workspaceInstance.current
        ) {
          workspaceInstance.current.dispose();

          workspaceInstance.current =
            null;
        }
      };
    }, []);

    /* =======================================================
       RESIZE BLOCKLY
    ======================================================= */

    useEffect(() => {
      const handleResize =
        () => {
          if (
            workspaceInstance.current
          ) {
            Blockly.svgResize(
              workspaceInstance.current
            );
          }
        };

      window.addEventListener(
        "resize",
        handleResize
      );

      const timer =
        setTimeout(
          handleResize,
          300
        );

      return () => {
        clearTimeout(timer);

        window.removeEventListener(
          "resize",
          handleResize
        );
      };
    }, [builderMode]);

    /* =======================================================
       CATEGORY
    ======================================================= */

    const categories = [
      "Purchase",
      "Stake",
      "Values",
      "Analysis",
      "Logic",
      "Math",
      "Flow",
      "Variables",
    ];
        /* =======================================================
       RENDER
    ======================================================= */

    return (
      <div className="auto-trade-page">
        <header className="auto-builder-header">
          <div className="auto-builder-brand">
            <div className="auto-builder-logo">
              M
            </div>

            <div className="auto-builder-title">
              BOT BUILDER
            </div>
          </div>

          <div className="auto-builder-actions">
            <button
              type="button"
              className="auto-back-button"
              onClick={() =>
                navigate(-1)
              }
              disabled={running}
            >
              ‹ Back
            </button>

            <button
              type="button"
              className="auto-save-button"
              onClick={saveBot}
              disabled={running}
            >
              Save
            </button>

            {!running ? (
              <button
                type="button"
                className="auto-run-button"
                onClick={runBot}
              >
                ▶ Run
              </button>
            ) : (
              <button
                type="button"
                className="auto-stop-button"
                onClick={stopBot}
              >
                ■ Stop
              </button>
            )}
          </div>
        </header>

        <div className="auto-trader-tabs">
          <button
            type="button"
            onClick={() =>
              navigate("/volatility")
            }
          >
            Manual Trader
          </button>

          <button
            type="button"
            onClick={() =>
              navigate("/bulk")
            }
          >
            Bulk Trader
          </button>

          <button
            type="button"
            className="active"
          >
            Auto Trader
          </button>
        </div>

        <div className="auto-market-ticker">
          {MARKETS.map((market) => {
            const live =
              liveMarkets[market.symbol];

            const isSelected =
              market.id ===
              selectedMarketId;

            const digit =
              live
                ? getLastDigit(
                    live.price,
                    market.decimals
                  )
                : "-";

            return (
              <button
                type="button"
                key={market.id}
                className={
                  isSelected
                    ? "auto-market-chip active"
                    : "auto-market-chip"
                }
                onClick={() => {
                  selectedMarketRef.current =
                    market.id;

                  setSelectedMarketId(
                    market.id
                  );
                }}
              >
                <strong>
                  {market.name}
                </strong>

                <span>
                  {live
                    ? Number(
                        live.price
                      ).toLocaleString(
                        "en-US",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      )
                    : "Loading..."}
                </span>

                <small>
                  {digit}
                </small>
              </button>
            );
          })}
        </div>

        <div className="auto-builder-toolbar">
          <div className="auto-toolbar-market">
            <label>MARKET</label>

            <select
              value={selectedMarketId}
              onChange={handleMarketChange}
            >
              {MARKETS.map((market) => (
                <option
                  key={market.id}
                  value={market.id}
                >
                  {market.name}
                </option>
              ))}
            </select>
          </div>

          <div className="auto-toolbar-price">
            <span>CURRENT TICK</span>

            <strong>
              {currentPrice !== null
                ? Number(
                    currentPrice
                  ).toLocaleString(
                    "en-US",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )
                : "—"}
            </strong>

            <small>
              LAST DIGIT {currentDigit}
            </small>
          </div>

          <div className="auto-toolbar-balance">
            <span>BALANCE</span>

            <strong>
              ${Number(balance).toFixed(2)}
            </strong>
          </div>
        </div>

        {builderMode && (
          <div className="auto-builder-area">
            <aside className="auto-category-nav">
              {categories.map((category) => (
                <button
                  type="button"
                  key={category}
                  className={
                    activeCategory === category
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    setActiveCategory(category)
                  }
                  onMouseDown={() => {
                    if (
                      workspaceInstance.current
                    ) {
                      const toolbox =
                        workspaceInstance.current.getToolbox();

                      if (toolbox) {
                        const item =
                          toolbox
                            .getToolboxItems()
                            .find(
                              (entry) =>
                                entry.getName?.() ===
                                category
                            );

                        if (item) {
                          toolbox.setSelectedItem(
                            item
                          );
                        }
                      }
                    }
                  }}
                >
                  {category}
                </button>
              ))}
            </aside>

            <main className="auto-blockly-wrapper">
              <div
                ref={workspaceRef}
                className="auto-blockly-workspace"
              />
            </main>
          </div>
        )}

        <section className="auto-signals">
          <div className="auto-section-heading">
            <div>
              <span className="signal-dot" />

              <strong>
                LIVE TRADING SIGNALS
              </strong>
            </div>

            <button
              type="button"
              onClick={() =>
                addLog("Signals rescanned.")
              }
            >
              ↻ Rescan
            </button>
          </div>

          <div className="auto-signal-card">
            <div className="signal-card-top">
              <div className="signal-rank">1</div>

              <div className="signal-market">
                <strong>
                  {selectedMarket.name}
                </strong>

                <span>1s live</span>
              </div>

              <div className="signal-contract">
                UNDER 8
              </div>
            </div>

            <div className="signal-confidence">
              <div>
                <span>CONFIDENCE</span>
                <strong>82.0%</strong>
              </div>

              <div className="confidence-bar">
                <span
                  style={{
                    width: "82%",
                  }}
                />
              </div>
            </div>

            <div className="signal-stats">
              <div>
                <span>WIN RATE</span>

                <strong>
                  {stats.wins + stats.losses > 0
                    ? (
                        (stats.wins /
                          (stats.wins +
                            stats.losses)) *
                        100
                      ).toFixed(1)
                    : "0.0"}
                  %
                </strong>
              </div>

              <div>
                <span>LAST DIGIT</span>
                <strong>{currentDigit}</strong>
              </div>

              <div>
                <span>TRADES</span>
                <strong>{stats.trades}</strong>
              </div>
            </div>

            <button
              type="button"
              className="signal-load-button"
              onClick={() =>
                loadXml(templates[0].xml)
              }
            >
              ⚡ Load UNDER 8 Signal
            </button>
          </div>
        </section>

        <section className="auto-log">
          <div className="auto-log-header">
            LOG
          </div>

          <div className="auto-log-list">
            {logs.length === 0 ? (
              <div className="auto-log-empty">
                Run a strategy to see live activity here.
              </div>
            ) : (
              logs.map((item) => (
                <div
                  key={item.id}
                  className={`auto-log-item ${item.type}`}
                >
                  <span>{item.time}</span>
                  <strong>{item.message}</strong>
                </div>
              ))
            )}
          </div>
        </section>

        <div className="auto-bottom-actions">
          <button
            type="button"
            onClick={() =>
              setShowLoad((value) => !value)
            }
          >
            📁 Load Bot
          </button>

          <button
            type="button"
            onClick={() =>
              setShowTemplates((value) => !value)
            }
          >
            ▱ Templates
          </button>
        </div>

        {showTemplates && (
          <div
            className="auto-modal-overlay"
            onClick={() =>
              setShowTemplates(false)
            }
          >
            <div
              className="auto-modal"
              onClick={(event) =>
                event.stopPropagation()
              }
            >
              <div className="auto-modal-header">
                <strong>Templates</strong>

                <button
                  type="button"
                  onClick={() =>
                    setShowTemplates(false)
                  }
                >
                  ×
                </button>
              </div>

              <div className="auto-template-grid">
                {templates.map((template) => (
                  <button
                    type="button"
                    key={template.id}
                    onClick={() => {
                      loadXml(template.xml);
                      setShowTemplates(false);
                    }}
                  >
                    <strong>{template.name}</strong>
                    <span>{template.description}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {showLoad && (
          <div
            className="auto-modal-overlay"
            onClick={() =>
              setShowLoad(false)
            }
          >
            <div
              className="auto-modal"
              onClick={(event) =>
                event.stopPropagation()
              }
            >
              <div className="auto-modal-header">
                <strong>Saved Bots</strong>

                <button
                  type="button"
                  onClick={() =>
                    setShowLoad(false)
                  }
                >
                  ×
                </button>
              </div>

              {savedBots.length === 0 ? (
                <div className="auto-no-bots">
                  No saved bots yet.
                </div>
              ) : (
                <div className="auto-saved-list">
                  {savedBots.map((bot) => (
                    <button
                      type="button"
                      key={bot.id}
                      onClick={() => {
                        loadXml(bot.xml);
                        setShowLoad(false);
                      }}
                    >
                      <strong>{bot.name}</strong>
                      <span>{bot.marketId}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <button
          type="button"
          className="auto-ai-button"
          onClick={() =>
            addLog("AI scanner ready.")
          }
        >
          <span>AI</span>
        </button>
      </div>
    );
  }
